/**
 * i18n Configuration — Internationalization setup for Scrudix
 *
 * Uses i18next with react-i18next. Translations are bundled from
 * /src/locales/{lang}.json for immediate availability in Vite builds.
 *
 * Supported languages: English (en), Spanish (es)
 * Add more by creating a new JSON file and adding the code to `supportedLngs`.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import bundled translations (keeps them in the JS bundle for instant availability)
import en from '../src/locales/en.json';
import es from '../src/locales/es.json';

export const supportedLanguages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
] as const;

export type SupportedLanguage = (typeof supportedLanguages)[number]['code'];

const STORAGE_KEY = 'scrudix_language';

/** Detect the user's preferred language */
function detectLanguage(): SupportedLanguage {
  // 1. Persisted preference
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && supportedLanguages.some((l) => l.code === stored)) {
    return stored as SupportedLanguage;
  }

  // 2. Browser language
  const browserLang = navigator.language.split('-')[0];
  if (supportedLanguages.some((l) => l.code === browserLang)) {
    return browserLang as SupportedLanguage;
  }

  // 3. Default
  return 'en';
}

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    es: { translation: es },
  },
  lng: detectLanguage(),
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false, // React already escapes
  },
  react: {
    useSuspense: false, // Don't suspend on initial render
  },
});

/** Persist language selection */
export function setLanguage(code: SupportedLanguage): void {
  localStorage.setItem(STORAGE_KEY, code);
  i18n.changeLanguage(code);
  document.documentElement.setAttribute('lang', code);
}

// Set initial lang attribute on <html>
document.documentElement.setAttribute('lang', i18n.language);

export default i18n;
